import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  TextField,
  Button,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import api from '../config/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const COLOR_LEGEND = [
  { color: '#0088FE', label: 'Blue - Primary Category' },
  { color: '#00C49F', label: 'Green - Secondary Category' },
  { color: '#FFBB28', label: 'Yellow - Tertiary Category' },
  { color: '#FF8042', label: 'Orange - Additional Category' },
  { color: '#8884d8', label: 'Purple - Extended Category' },
  { color: '#82ca9d', label: 'Teal - Extended Category' },
];

function Dashboard() {
  const [stats, setStats] = useState({
    trainers: { total: 0, by_service_type: [], by_category: [] },
    clients: { total: 0, by_service_type: [] },
    revenue: { total: 0, total_purchased_plans: 0, total_spent: 0, net: 0, by_type: [], monthly_trend: [] },
  });
  const [loading, setLoading] = useState(true);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [filterStartDate, filterEndDate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);

      const response = await api.get(`/dashboard/stats?${params.toString()}`);
      setStats(response.data.data || {
        trainers: { total: 0, by_service_type: [], by_category: [] },
        clients: { total: 0, by_service_type: [] },
        revenue: { total: 0, total_purchased_plans: 0, total_spent: 0, net: 0, by_type: [], monthly_trend: [] },
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const statCards = [
    { title: 'Total Trainers', value: stats.trainers.total, color: '#C8E6C9' },
    { title: 'Total Clients', value: stats.clients.total, color: '#BBDEFB' },
    { title: 'Total Purchased Amount', value: stats.revenue.total.toFixed(2), color: '#E1BEE7' },
    { title: 'Purchased Plans Count', value: stats.revenue.total_purchased_plans, color: '#FFE0B2' },
    // { title: 'Profit', value: `₹${stats.revenue.total_spent.toFixed(2)}`, color: '#FFE0B2' },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Dashboard</Typography>
      </Box>

      {/* Date Range Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Date Range Filter</Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Start Date"
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                backgroundColor: card.color,
                color: '#000000',
              }}
            >
              <Typography variant="h3" component="div">
                {card.value}
              </Typography>
              <Typography variant="h6">{card.title}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Trainers by Service Type Pie Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Trainers by Service Type</Typography>
              <Tooltip title="Shows the distribution of trainers across different service types (e.g., Home Services, Online Services)">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              This chart displays how trainers are distributed across different service types. Each color represents a different service type category.
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.trainers.by_service_type}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ service_type_name, percent }) => `${service_type_name || 'Not Set'}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {stats.trainers.by_service_type.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  formatter={(value, name, props) => [
                    `${value} trainers`,
                    props.payload.service_type_name || 'Not Set'
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Color Legend:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {stats.trainers.by_service_type.map((entry, index) => (
                  <Chip
                    key={index}
                    label={entry.service_type_name || 'Not Set'}
                    size="small"
                    sx={{
                      backgroundColor: COLORS[index % COLORS.length],
                      color: 'white',
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Clients by Service Type Pie Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Clients by Service Type</Typography>
              <Tooltip title="Shows the distribution of clients (users) across different service types">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              This chart displays how clients are distributed across different service types. Helps identify which service types are most popular among users.
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.clients.by_service_type}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ service_type_name, percent }) => `${service_type_name || 'Not Set'}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {stats.clients.by_service_type.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  formatter={(value, name, props) => [
                    `${value} clients`,
                    props.payload.service_type_name || 'Not Set'
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Color Legend:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {stats.clients.by_service_type.map((entry, index) => (
                  <Chip
                    key={index}
                    label={entry.service_type_name || 'Not Set'}
                    size="small"
                    sx={{
                      backgroundColor: COLORS[index % COLORS.length],
                      color: 'white',
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Trainers by Category Pie Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Trainers by Category</Typography>
              <Tooltip title="Shows the distribution of trainers across different categories (e.g., Home, Arotho, Physical)">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              This chart displays how trainers are distributed across different categories. Each segment represents a category with its trainer count.
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.trainers.by_category}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ category_name, percent }) => `${category_name || 'Not Set'}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {stats.trainers.by_category.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  formatter={(value, name, props) => [
                    `${value} trainers`,
                    props.payload.category_name || 'Not Set'
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Color Legend:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {stats.trainers.by_category.map((entry, index) => (
                  <Chip
                    key={index}
                    label={entry.category_name || 'Not Set'}
                    size="small"
                    sx={{
                      backgroundColor: COLORS[index % COLORS.length],
                      color: 'white',
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Revenue by Type Bar Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Revenue by Transaction Type</Typography>
              <Tooltip title="Shows total revenue (credits) and spending (debits) by transaction type">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              This bar chart displays revenue breakdown by transaction type. Credit transactions show money coming in, while debit transactions show money going out. Values are in Coins.
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.revenue.by_type}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="transaction_type" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="total_amount" fill="#8884d8" name="Amount (Coins)" />
              </BarChart>
            </ResponsiveContainer>
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary">
                <strong>Credit:</strong> Money/Coins added to accounts | <strong>Debit:</strong> Money/Coins deducted from accounts
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Monthly revenue & spend — bar chart by month (last 6 months) */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Monthly Revenue</Typography>
              <Tooltip title="Month-wise bars: credits (revenue) vs debits (spent) for the last six months">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Each month shows two bars: revenue (wallet credits) and spent (wallet debits), in coins. Compare months side by side to see activity over time.
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={stats.revenue.monthly_trend} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="revenue" fill="#8884d8" name="Revenue (Coins)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="spent" fill="#82ca9d" name="Spent (Coins)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <Box mt={2} display="flex" gap={2} flexWrap="wrap">
              <Chip
                label="Purple: Revenue (Credits)"
                size="small"
                sx={{ backgroundColor: '#8884d8', color: 'white' }}
              />
              <Chip
                label="Green: Spent (Debits)"
                size="small"
                sx={{ backgroundColor: '#82ca9d', color: 'white' }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;

